import type { DisplayStatus, Role } from "@/lib/auth/access";
import { putSetting as putSettingValue } from "@/lib/api/endpoints";

export type LoadedSettings = {
  settings: Record<string, unknown>;
  masked?: Record<string, true>;
  templateCount?: number;
  /** From GET /api/settings `capabilities.localClaude` (AI_LOCAL_CLI=true). */
  localClaude?: boolean;
};

export type AccessUserRow = {
  email: string;
  role: Role;
  status: DisplayStatus;
  addedAt: string;
  isUser: boolean;
  isBootstrapAdmin?: boolean;
};

export type AccessData =
  | { isSuperAdmin: false }
  | {
      isSuperAdmin: true;
      currentEmail: string;
      envSuperAdmins: string[];
      users: AccessUserRow[];
    };

export function asString(value: unknown, fallback: string): string {
  return typeof value === "string" ? value : fallback;
}

export async function putSetting(key: string, value: unknown): Promise<void> {
  try {
    await putSettingValue(key, value);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    throw new Error(`Failed to save ${key}: ${message}`);
  }
}

export type TestResult = {
  ok: boolean;
  models: string[];
  checkedAt: string;
  error?: string;
};

type StoredAccessibleModels = {
  ok?: boolean;
  models?: unknown;
  checkedAt?: string;
  error?: string;
};

export function asTestResult(value: unknown): TestResult | null {
  if (!value || typeof value !== "object") return null;
  const v = value as StoredAccessibleModels;
  if (typeof v.ok !== "boolean") return null;
  const models = Array.isArray(v.models)
    ? v.models.filter((m): m is string => typeof m === "string")
    : [];
  return {
    ok: v.ok,
    models,
    checkedAt: typeof v.checkedAt === "string" ? v.checkedAt : new Date().toISOString(),
    error: typeof v.error === "string" ? v.error : undefined,
  };
}

export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const diffMs = Date.now() - then;
  if (diffMs < 0) return "just now";
  const sec = Math.floor(diffMs / 1000);
  if (sec < 5) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}
